import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icon } from '../../foundation/primitives';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { SuccessEffect, FailureEffect } from '../../effects';

/**
 * Template17: 逃走タイミング（TimingEscape）- ホラーテイスト
 * タイミングよくタップして追跡者から逃げる
 * 失敗するごとに画面が暗くなっていく演出付き
 */
const Template17TimingEscape = ({
  hostSubmit,
  hostConfig,
  registryId = 'minigame_timingescape_17',
  templateId = 17
}) => {
  // T11〜T14 と同じ configKey パターン: hostConfig 未指定時に `?? {}` が毎レンダー新オブジェクトに
  // なっても、文字列キー比較で resolvedConfig の同一性を保ち、再レンダーループを防ぐ
  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const config = hostConfig ?? {};
    const safeNumber = (value, fallback) => (Number.isFinite(value) ? value : fallback);
    return {
      title: config.title ?? '',
      instruction: config.instruction ?? '逃げろ！',
      requiredSuccesses: safeNumber(config.requiredSuccesses, 5),
      totalAttempts: safeNumber(config.totalAttempts, 8),
      windowSize: safeNumber(config.windowSize, 200),
      indicatorSpeed: safeNumber(config.indicatorSpeed, 1500),
      perfectWindowSize: safeNumber(config.perfectWindowSize, 80),
      chaserVisual: {
        emoji: config.chaserVisual?.emoji ?? '👹',
        name: config.chaserVisual?.name ?? '追跡者'
      },
      playerVisual: {
        emoji: config.playerVisual?.emoji ?? '🏃',
        name: config.playerVisual?.name ?? 'あなた'
      },
      distanceDisplay: {
        showDistance: config.distanceDisplay?.showDistance !== false,
        initialDistance: safeNumber(config.distanceDisplay?.initialDistance, 50),
        successGain: safeNumber(config.distanceDisplay?.successGain, 15),
        perfectGain: safeNumber(config.distanceDisplay?.perfectGain, 25),
        failPenalty: safeNumber(config.distanceDisplay?.failPenalty, 20),
        escapeDistance: safeNumber(config.distanceDisplay?.escapeDistance, 100),
        caughtDistance: safeNumber(config.distanceDisplay?.caughtDistance, 0)
      },
      // ホラー演出設定
      horrorMode: config.horrorMode !== false,
      maxDarkness: safeNumber(config.maxDarkness, 0.85)
    };
  }, [configKey]);

  const backgroundStyle = useMinigameBackground(hostConfig);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const initializedRef = useRef(false);

  const [gameState, setGameState] = useState('ready'); // 'ready' | 'playing' | 'waiting' | 'result'
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [successes, setSuccesses] = useState(0);
  const [perfects, setPerfects] = useState(0);
  const [failures, setFailures] = useState(0);
  const [distance, setDistance] = useState(resolvedConfig.distanceDisplay.initialDistance);
  const [indicatorPosition, setIndicatorPosition] = useState(0);
  const [indicatorDirection, setIndicatorDirection] = useState(1);
  const [attemptResult, setAttemptResult] = useState(null); // 'success' | 'perfect' | 'fail' | null
  const [showEffect, setShowEffect] = useState(null);
  const [screenShake, setScreenShake] = useState(false);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(null);

  // 失敗に応じた暗さを計算
  const darknessLevel = useMemo(() => {
    const maxFailures = resolvedConfig.totalAttempts - resolvedConfig.requiredSuccesses;
    if (maxFailures <= 0) return 0;
    const ratio = failures / maxFailures;
    return Math.min(ratio * resolvedConfig.maxDarkness, resolvedConfig.maxDarkness);
  }, [failures, resolvedConfig.totalAttempts, resolvedConfig.requiredSuccesses, resolvedConfig.maxDarkness]);

  // インジケーターアニメーション
  useEffect(() => {
    if (gameState !== 'playing') return;

    const animate = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const delta = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      setIndicatorPosition(prev => {
        const speed = 100 / resolvedConfig.indicatorSpeed * delta;
        let newPos = prev + speed * indicatorDirection;

        // 端で反転
        if (newPos >= 100) {
          newPos = 100;
          setIndicatorDirection(-1);
        } else if (newPos <= 0) {
          newPos = 0;
          setIndicatorDirection(1);
        }

        return newPos;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      lastTimeRef.current = null;
    };
  }, [gameState, indicatorDirection, resolvedConfig.indicatorSpeed]);

  // タップ処理
  const handleTap = useCallback(() => {
    if (gameState !== 'playing') return;

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    // 50が中央（成功ゾーン）
    const distanceFromCenter = Math.abs(indicatorPosition - 50);
    const halfWindow = resolvedConfig.windowSize / 2 / 100 * 50;
    const halfPerfectWindow = resolvedConfig.perfectWindowSize / 2 / 100 * 50;

    let result;
    let distanceChange;

    if (distanceFromCenter <= halfPerfectWindow) {
      result = 'perfect';
      distanceChange = resolvedConfig.distanceDisplay.perfectGain;
      setSuccesses(prev => prev + 1);
      setPerfects(prev => prev + 1);
    } else if (distanceFromCenter <= halfWindow) {
      result = 'success';
      distanceChange = resolvedConfig.distanceDisplay.successGain;
      setSuccesses(prev => prev + 1);
    } else {
      result = 'fail';
      distanceChange = -resolvedConfig.distanceDisplay.failPenalty;
      setFailures(prev => prev + 1);
      // 失敗時の画面揺れ
      setScreenShake(true);
      setTimeout(() => setScreenShake(false), 300);
    }

    setAttemptResult(result);
    setDistance(prev => Math.max(0, Math.min(100, prev + distanceChange)));
    setGameState('waiting');
    setCurrentAttempt(prev => prev + 1);
  }, [gameState, indicatorPosition, resolvedConfig]);

  // 次のアテンプトまたはゲーム終了
  useEffect(() => {
    if (gameState !== 'waiting') return;

    const timer = setTimeout(() => {
      const newDistance = distance;

      // 逃げ切り成功
      if (newDistance >= resolvedConfig.distanceDisplay.escapeDistance) {
        setGameState('result');
        setShowEffect('success');
        return;
      }

      // 捕まった
      if (newDistance <= resolvedConfig.distanceDisplay.caughtDistance) {
        setGameState('result');
        setShowEffect('fail');
        return;
      }

      // アテンプト終了
      if (currentAttempt >= resolvedConfig.totalAttempts) {
        if (successes >= resolvedConfig.requiredSuccesses) {
          setGameState('result');
          setShowEffect('success');
        } else {
          setGameState('result');
          setShowEffect('fail');
        }
        return;
      }

      // 次のアテンプト
      setAttemptResult(null);
      setIndicatorPosition(0);
      setIndicatorDirection(1);
      lastTimeRef.current = null;
      setGameState('playing');
    }, 800);

    return () => clearTimeout(timer);
  }, [gameState, currentAttempt, distance, successes, resolvedConfig]);

  // エフェクト終了後の送信
  useEffect(() => {
    if (showEffect) {
      const timer = setTimeout(() => {
        const finalResult = distance >= resolvedConfig.distanceDisplay.escapeDistance ||
          successes >= resolvedConfig.requiredSuccesses ? 'success' : 'fail';

        submit(finalResult, {
          successes,
          perfects,
          failures,
          totalAttempts: currentAttempt,
          finalDistance: Math.round(distance)
        });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [showEffect, submit, successes, perfects, failures, currentAttempt, distance, resolvedConfig]);

  // ゲーム開始
  const startGame = useCallback(() => {
    setGameState('playing');
  }, []);

  // リセット（T11〜T14 と同じ initializedRef ガード: 初期化は初回マウント時のみ実行）
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setGameState('ready');
    setCurrentAttempt(0);
    setSuccesses(0);
    setPerfects(0);
    setFailures(0);
    setDistance(resolvedConfig.distanceDisplay.initialDistance);
    setIndicatorPosition(0);
    setIndicatorDirection(1);
    setAttemptResult(null);
    setShowEffect(null);
    setScreenShake(false);
    lastTimeRef.current = null;
    resetSubmission();
  }, [resolvedConfig, resetSubmission]);

  // 成功ゾーンの位置計算
  const successZoneWidth = resolvedConfig.windowSize / 2;
  const perfectZoneWidth = resolvedConfig.perfectWindowSize / 2;

  // 背景スタイル（画像があれば使用、なければトークンの --mg-bg を .t17-root で適用）
  const containerStyle = useMemo(() => {
    if (backgroundStyle) {
      return backgroundStyle;
    }
    return {};
  }, [backgroundStyle]);

  return (
    <div
      className="t17-root relative w-full h-full flex flex-col items-center justify-center p-4 overflow-hidden"
      style={{
        ...containerStyle,
        animation: screenShake ? 't17shake 0.3s ease-in-out' : undefined
      }}
      onClick={gameState === 'playing' ? handleTap : undefined}
    >
      {/* 暗くなるオーバーレイ（ホラー演出） */}
      {resolvedConfig.horrorMode && (
        <div
          className="absolute inset-0 pointer-events-none transition-opacity duration-500 z-10"
          style={{
            backgroundColor: '#000',
            opacity: darknessLevel
          }}
        />
      )}

      {/* 角からの暗闘（ホラー演出強化） */}
      {resolvedConfig.horrorMode && (
        <>
          <div
            className="absolute inset-0 pointer-events-none z-10"
            style={{
              background: `radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,${darknessLevel * 0.8}) 100%)`,
            }}
          />
          {/* 赤い縁取り（危険度に応じて） */}
          <div
            className="absolute inset-0 pointer-events-none z-10 transition-opacity duration-300"
            style={{
              boxShadow: `inset 0 0 ${50 + darknessLevel * 100}px ${darknessLevel * 30}px rgba(139, 0, 0, ${darknessLevel * 0.5})`,
              opacity: darknessLevel > 0.2 ? 1 : 0
            }}
          />
        </>
      )}

      {/* エフェクト */}
      <div className="z-30">
        {showEffect === 'success' && <SuccessEffect />}
        {showEffect === 'fail' && <FailureEffect />}
      </div>

      {/* メインコンテンツ */}
      <div className="relative z-20 flex flex-col items-center">
        {/* タイトル - ホラー風 */}
        {resolvedConfig.title && (
          <h2 className="t17-title text-3xl font-bold mb-2">
            {resolvedConfig.title}
          </h2>
        )}

        {/* 説明 */}
        <p className="t17-instruction mb-4 text-center text-lg font-medium">
          {resolvedConfig.instruction}
        </p>

        {/* 距離メーター */}
        {resolvedConfig.distanceDisplay.showDistance && (
          <div className="w-full max-w-md mb-6">
            <div className="flex justify-between items-center mb-1">
              <span className="text-3xl filter drop-shadow-lg">{resolvedConfig.chaserVisual.emoji}</span>
              <span className="t17-distance-label text-sm font-mono">距離: {Math.round(distance)}m</span>
              <span className="text-3xl filter drop-shadow-lg">{resolvedConfig.playerVisual.emoji}</span>
            </div>
            <div className="t17-gauge-track w-full h-4 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-300 rounded-full"
                style={{
                  width: `${distance}%`,
                  background: `linear-gradient(90deg, #8b0000 0%, #dc2626 50%, #22c55e 100%)`
                }}
              />
            </div>
            <div className="t17-gauge-caption flex justify-between text-xs mt-1 font-medium">
              <span className="flex items-center gap-1">
                <Icon name="Skull" size={12} /> 捕獲
              </span>
              <span>逃走成功</span>
            </div>
          </div>
        )}

        {/* 進捗表示 */}
        <div className="flex items-center gap-4 mb-4">
          <div className="t17-chip flex items-center gap-1 px-3 py-1 rounded-lg">
            <Icon name="Target" size={16} className="t17-icon-accent" />
            <span className="t17-count font-mono">
              {successes} / {resolvedConfig.requiredSuccesses}
            </span>
          </div>
          <div className="t17-chip flex items-center gap-1 px-3 py-1 rounded-lg">
            <Icon name="Footprints" size={16} className="t17-icon-muted" />
            <span className="t17-count-muted font-mono">
              {currentAttempt} / {resolvedConfig.totalAttempts}
            </span>
          </div>
        </div>

        {/* タイミングバー - ホラー風 */}
        <div className="t17-timing-bar w-full max-w-md h-20 rounded-xl relative overflow-hidden mb-6 border-2">
          {/* 成功ゾーン */}
          <div
            className="absolute top-0 h-full"
            style={{
              left: `${50 - successZoneWidth}%`,
              width: `${successZoneWidth * 2}%`,
              background: 'linear-gradient(180deg, rgba(220,38,38,0.3) 0%, rgba(139,0,0,0.5) 100%)'
            }}
          />
          {/* パーフェクトゾーン */}
          <div
            className="absolute top-0 h-full"
            style={{
              left: `${50 - perfectZoneWidth}%`,
              width: `${perfectZoneWidth * 2}%`,
              background: 'linear-gradient(180deg, rgba(220,38,38,0.5) 0%, rgba(139,0,0,0.8) 100%)'
            }}
          />
          {/* 中央線 */}
          <div className="t17-center-line absolute top-0 left-1/2 w-1 h-full" />

          {/* インジケーター - 赤い丸 */}
          <div
            className="t17-indicator absolute top-2 bottom-2 w-4 rounded-full shadow-lg transition-transform"
            style={{
              left: `${indicatorPosition}%`,
              transform: 'translateX(-50%)',
              boxShadow: '0 0 20px rgba(220, 38, 38, 0.8), 0 0 40px rgba(139, 0, 0, 0.5)'
            }}
          />

          {/* 結果表示 */}
          {attemptResult && (
            <div className="t17-result-overlay absolute inset-0 flex items-center justify-center">
              <span className={`text-3xl font-bold animate-pulse ${
                attemptResult === 'perfect' ? 't17-feedback--perfect' :
                attemptResult === 'success' ? 't17-feedback--success' :
                't17-feedback--miss'
              }`}>
                {attemptResult === 'perfect' ? '見切った！' :
                 attemptResult === 'success' ? '届いた' :
                 '逃した…'}
              </span>
            </div>
          )}
        </div>

        {/* タップ指示 */}
        {gameState === 'playing' && (
          <div className="text-center animate-pulse">
            <Icon name="Zap" size={40} className="t17-zap-icon mx-auto mb-2" />
            <p className="t17-tap-text text-xl font-bold">画面をタップ！</p>
          </div>
        )}

        {/* スタートボタン */}
        {gameState === 'ready' && (
          <button
            onClick={startGame}
            className="t17-start-btn px-8 py-4 font-bold text-xl rounded-xl transition-all active:scale-95 border-2"
          >
            スタート
          </button>
        )}

        {/* 結果表示 */}
        {gameState === 'result' && (
          <div className="text-center" style={{ animation: 't17fadeIn 0.3s ease-out' }}>
            <p className={`text-4xl font-bold mb-2 ${
              distance >= resolvedConfig.distanceDisplay.escapeDistance ||
              successes >= resolvedConfig.requiredSuccesses
                ? 't17-result--win'
                : 't17-result--lose'
            }`}>
              {distance >= resolvedConfig.distanceDisplay.escapeDistance ||
               successes >= resolvedConfig.requiredSuccesses
                ? '逃走成功！'
                : '捕まった...'}
            </p>
            <p className="t17-result-sub">
              成功: {successes} / パーフェクト: {perfects}
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes t17fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes t17shake {
          0%, 100% { transform: translate(0, 0); }
          10%, 30%, 50%, 70%, 90% { transform: translate(-3px, 2px); }
          20%, 40%, 60%, 80% { transform: translate(3px, -2px); }
        }
      `}</style>
    </div>
  );
};

export default Template17TimingEscape;
